import MainDispatcher from '../support/MainDispatcher';
import server from '../support/server';
import dash_api from '../support/dash_api';
import { ReduxAsyncAction } from '../support/utils';

import type { Permissions, Filters, UserData } from '../types/accessControlTypes';

import { simplifyPermissions, extractFeatureFilters } from '../../common/support/utils/accessControlHelpers';
import { ErrorProvider } from '../../../libraries/branch-tooling-next/Error/ErrorProvider';

const TeamMembersLoad = new ReduxAsyncAction('TEAM_MEMBERS_LOAD');
TeamMembersLoad.registerRequest(() => {
  return dash_api.get('/v1/dashboard-user/team').then(({ data }) => data);
});
export const loadTeamMembers = TeamMembersLoad.request;
export const TEAM_MEMBERS_LOAD = TeamMembersLoad.constants;

const errorProvider = new ErrorProvider();

const SaveTeamMember = new ReduxAsyncAction('SAVE_TEAM_MEMBER');
SaveTeamMember.registerRequest((userData: UserData) => {
  MainDispatcher.dispatch({
    action: 'TEAM_ADDING',
  });
  return dash_api
    .post('/v1/dashboard-user/invite', userData)
    .then(({ data }) => {
      MainDispatcher.dispatch({
        action: 'TEAM_ADDED',
        team_member: userData,
      });
      return data;
    })
    .catch((err) => {
      MainDispatcher.dispatch({
        action: 'TEAM_ERROR',
        error: errorProvider.parse(err).getMessageOrEmpty() || "Error inviting team member'",
      });
      throw err;
    });
});
export const saveTeamMember = SaveTeamMember.request;
export const SAVE_TEAM_MEMBER = SaveTeamMember.constants;

const DeleteTeamMember = new ReduxAsyncAction('DELETE_TEAM_MEMBER');
DeleteTeamMember.registerRequest((userId: string, apps?: Array<string>, resetAllApps?: boolean) => {
  return dash_api
    .delete('/v1/dashboard-user', {
      data: { userId, apps, resetAllApps },
    })
    .then(() => {
      MainDispatcher.dispatch({ action: 'TEAM_MEMBER_DELETED' });
      return { id: userId, deleted: true };
    })
    .catch((err) => {
      MainDispatcher.dispatch({
        action: 'TEAM_ERROR',
        error: errorProvider.parse(err).getFieldEmpty() || 'Failed to delete team member',
      });
    });
});
export const deleteTeamMember = DeleteTeamMember.request;
export const DELETE_TEAM_MEMBER = DeleteTeamMember.constants;

const UndeleteTeamMember = new ReduxAsyncAction('UNDELETE_TEAM_MEMBER');
UndeleteTeamMember.registerRequest((userId: string) => {
  return dash_api.put('/v1/dashboard-user/undelete', { userId }).then(() => {
    MainDispatcher.dispatch({ action: 'TEAM_MEMBER_UNDELETED' });
    return { id: userId, deleted: false };
  });
});
export const undeleteTeamMember = UndeleteTeamMember.request;
export const UNDELETE_TEAM_MEMBER = UndeleteTeamMember.constants;

const ResetToInherit = new ReduxAsyncAction('RESET_TEAM_MEMBER_TO_INHERIT');
ResetToInherit.registerRequest((userId: string) => {
  return dash_api.post('/v1/dashboard-user/reset-to-inherit', { userId }).then(() => {
    MainDispatcher.dispatch({ action: 'TEAM_MEMBER_RESET_TO_INHERIT' });
    return { id: userId };
  });
});
export const resetToInheritWithoutReload = ResetToInherit.request;
export const RESET_TEAM_MEMBER_TO_INHERIT = ResetToInherit.constants;

export const resetToInherit = (userId: string) => (dispatch: Function) => {
  return dispatch(resetToInheritWithoutReload(userId))
    .then(() => dispatch(loadTeamMembers()))
    .catch(() => {
      MainDispatcher.dispatch({
        action: 'TEAM_ERROR',
        error: 'Failed to reset team member to inherit',
      });
    });
};

const LoadByEmail = new ReduxAsyncAction('LOAD_BY_EMAIL');
LoadByEmail.registerRequest((email: string) => {
  return dash_api.get('/v1/dashboard-user', { params: { email } }).then(({ data }) => data);
});
export const loadByEmail = LoadByEmail.request;
export const LOAD_BY_EMAIL = LoadByEmail.constants;

const ResendInvite = new ReduxAsyncAction('RESEND_INVITE');
ResendInvite.registerRequest((userId: string) => {
  return dash_api.post('/v1/dashboard-user/resend', { userId }).then(({ data }) => {
    MainDispatcher.dispatch({
      action: 'TEAM_INVITE_RESENT',
    });
    return data;
  });
});
export const resendInvite = ResendInvite.request;
export const RESEND_INVITE = ResendInvite.constants;

const GetPermissionsForUser = new ReduxAsyncAction('GET_PERMISSIONS');
GetPermissionsForUser.registerRequest((userId: string) => {
  return dash_api.get('/v1/dashboard-user/team/permissions', { params: { userId } }).then(({ data }) => ({
    [userId]: {
      permissions: simplifyPermissions(data),
      featureFilters: extractFeatureFilters(data),
    },
  }));
});
export const getPermissionsForUser = GetPermissionsForUser.request;
export const GET_PERMISSIONS = GetPermissionsForUser.constants;

const UpdateTeamMember = new ReduxAsyncAction('UPDATE_TEAM_MEMBER');
UpdateTeamMember.registerRequest(
  (
    userId: string,
    name: string,
    accessRoleId: string,
    permissions: Permissions,
    filters: Filters,
    apps?: Array<string>,
    resetAllApps?: boolean,
    agencyId?: string
  ) => {
    return dash_api
      .put('/v1/dashboard-user', {
        userId,
        name,
        accessRoleId,
        permissions,
        filters,
        apps,
        resetAllApps,
        agencyId,
      })
      .then(({ data }) => data);
  }
);
export const updateTeamMember = UpdateTeamMember.request;
export const UPDATE_TEAM_MEMBER = UpdateTeamMember.constants;

export const updateAndReloadMembers =
  (
    userId: string,
    name: string,
    accessRoleId: string,
    permissions: Permissions,
    filters: Filters,
    apps?: Array<string>,
    resetAllApps?: boolean
  ) =>
  (dispatch: Function) => {
    return dispatch(updateTeamMember(userId, name, accessRoleId, permissions, filters, apps, resetAllApps))
      .then(() => dispatch(loadTeamMembers()))
      .catch((err) => {
        MainDispatcher.dispatch({
          action: 'TEAM_ERROR',
          error: errorProvider.parse(err).getMessageOrEmpty(),
        });
      });
  };

export const saveAndReloadMembers = (userData: UserData) => (dispatch: Function) => {
  return dispatch(saveTeamMember(userData))
    .then(() => dispatch(loadTeamMembers()))
    .catch((err) => {
      MainDispatcher.dispatch({
        action: 'TEAM_ERROR',
        error: errorProvider.parse(err).getMessageOrEmpty(),
      });
    });
};

export default {
  loadByEmail: function (email) {
    MainDispatcher.dispatch({
      action: 'TEAM_EMAIL_LOADING',
    });
    return server('team loadByEmail', { email: email }).then(
      function (user) {
        MainDispatcher.dispatch({
          action: 'TEAM_EMAIL_LOADED',
        });
        return user;
      },
      function (err) {
        MainDispatcher.dispatch({
          action: 'TEAM_ERROR',
          error: errorProvider.parse(err).getMessageOrEmpty(),
        });
        // eslint-disable-next-line
        throw errorProvider.parse(err).getResponse();
      }
    );
  },
};
